/*
 * Content that opens in place under the control that opened it (a disclosure): it unfolds from
 * that edge and folds back into it when `open` turns false, and what is below moves with it
 * (styles/index.css, .motion-unfold). Opened again while folding, it turns back from where it is.
 * While it folds it is inert, so focus can't land in what is leaving.
 *
 * In a flex column with a gap, pass the gap: the space above the content then unfolds with it
 * (the parent's gap is taken back), instead of appearing all at once when it mounts.
 * Open from the start (a page that loads with it open), it is simply there: only what a person
 * opens unfolds. It clips only while it moves: once open (data-settled), a menu inside it (a
 * filter's drop-down) reaches past its edge.
 */
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { usePresence } from './usePresence'

export function Unfold({ open, children, id, className, gap = 0 }: { open: boolean; children: ReactNode; id?: string; className?: string; gap?: number }) {
    const presence = usePresence(open, 220)
    const [instant, setInstant] = useState(open)
    const [settled, setSettled] = useState(open)
    // Folding (or opened again while folding): it clips again while it moves.
    if (!open && settled) setSettled(false)
    useEffect(() => {
        if (!open || settled) return
        const timer = setTimeout(() => setSettled(true), 220)
        return () => clearTimeout(timer)
    }, [open, settled])
    useEffect(() => {
        if (!instant) return
        const frame = requestAnimationFrame(() => setInstant(false))
        return () => cancelAnimationFrame(frame)
    }, [instant])
    if (!presence.mounted) return null
    return (
        <div id={id} data-state={presence.state} data-settled={settled || undefined} inert={!open || undefined} className="motion-unfold"
            style={{ ...(gap ? { marginTop: -gap } : {}), ...(instant ? { transition: 'none' } : {}) }}>
            <div>
                <div style={gap ? { paddingTop: gap } : undefined}>
                    <div className={className}>{children}</div>
                </div>
            </div>
        </div>
    )
}
